import {
  ApolloQueryResult,
  OperationVariables,
  useMutation
} from '@apollo/client'
import { Button, Typography, useMediaQuery, useTheme } from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { RESET_CART_MUTATION } from '../../graphql/cartCrud'
import { cleanCartData } from '../../helpers/userData'
import { getTranslate, lang } from '../../localization'
import { Cart, CartItem, CartResponse, PromotionIntent } from '../../types/cart'
import PromotionSection from './components/PromotionSection'
import SelectedRoomCard from './components/SelectedRoomCard'
import { ValidatedPromotionCode } from './promotionReservationIntent'
import useStyles from './styles/index.styles'

type StepOneProps = {
  cart: Cart | undefined
  cartID: string
  refetchCart: (variables?: Partial<OperationVariables> | undefined) => Promise<
    ApolloQueryResult<{
      cartResponse: CartResponse
    }>
  >
  clickedItemHash: string
  setClickedItemHash: React.Dispatch<React.SetStateAction<string>>
  loadingForGetCart: boolean
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>
  setRedirectLink: React.Dispatch<React.SetStateAction<string>>
  setRedirectTitle: React.Dispatch<React.SetStateAction<string>>
  setIsTimeExpired: React.Dispatch<React.SetStateAction<boolean>>
  promotionIntent: PromotionIntent
  setPromotionIntent: React.Dispatch<React.SetStateAction<PromotionIntent>>
  promotionCartKey: string
  onValidatedPromotionCodeChange: (validated?: ValidatedPromotionCode) => void
}

const StepOne = ({
  cart,
  cartID,
  refetchCart,
  loadingForGetCart,
  setShowModal,
  setRedirectLink,
  clickedItemHash,
  setClickedItemHash,
  setRedirectTitle,
  setIsTimeExpired,
  promotionIntent,
  setPromotionIntent,
  promotionCartKey,
  onValidatedPromotionCodeChange
}: StepOneProps) => {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [filteredItems, setFilteredItems] = useState<CartItem[]>([])
  const params = useParams<{ channelId: string; code: string; name: string }>()

  useEffect(() => {
    if (cart) {
      setFilteredItems(cart.items)
      setIsTimeExpired(false)
      setShowModal(false)
    }
  }, [cart])

  const activeItems = useMemo(
    () => filteredItems.filter((item) => item.quantity > 0),
    [filteredItems]
  )

  //RESET_CART_MUTATION
  const [resetCart] = useMutation(RESET_CART_MUTATION, {
    notifyOnNetworkStatusChange: true,
    onCompleted: () => {
      setShowModal(true)
      setRedirectTitle(
        getTranslate(
          'Cart has been empty. you will be redirect to booking page'
        )
      )
      setRedirectLink(
        `/${lang}/book/${params.channelId}/${params.code}/${params.name}`
      )
      cleanCartData()
      localStorage.removeItem('confirmPrimitiveInput')
      localStorage.removeItem('confirmPrimitiveOutput')
      localStorage.removeItem('hotelDetails')
      localStorage.removeItem('primitiveData')
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  return (
    <>
      {filteredItems.length > 0 ? (
        <div className={classes.header}>
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            className={classes.blackText}>
            {getTranslate('Selected Room(s)')}
          </Typography>
          <Button
            variant="text"
            startIcon={<DeleteIcon />}
            classes={{ startIcon: classes.blueIcon }}
            onClick={() => resetCart({ variables: { cartID: cartID } })}>
            <Typography
              variant={isMobile ? 'h5' : 'h4'}
              className={classes.blueText}>
              {getTranslate('Clear Shopping Cart')}
            </Typography>
          </Button>
        </div>
      ) : (
        <></>
      )}
      {activeItems.map((item, index) => (
        <SelectedRoomCard
          key={index}
          cartID={cartID}
          cartItem={item}
          clickedItemHash={clickedItemHash}
          setClickedItemHash={setClickedItemHash}
          refetchCart={refetchCart}
          loadingForGetCart={loadingForGetCart}
          setShowModal={setShowModal}
          setRedirectLink={setRedirectLink}
          setRedirectTitle={setRedirectTitle}
        />
      ))}
      {activeItems.length > 0 ? (
        <PromotionSection
          intent={promotionIntent}
          setIntent={setPromotionIntent}
          cartID={cartID}
          cartItems={activeItems}
          disabled={loadingForGetCart}
          cartKey={promotionCartKey}
          onValidatedCodeChange={onValidatedPromotionCodeChange}
        />
      ) : (
        <></>
      )}
    </>
  )
}

export default StepOne
